import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Image as ImageIcon,
  X,
  Download,
  Calendar,
  Filter,
  Search,
  Eye,
  Zap,
  ChevronLeft,
  ChevronRight,
  Grid3x3,
  List,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

export interface Photo {
  id: string;
  dataUrl: string;
  timestamp: string;
  type: 'gauge' | 'equipment' | 'maintenance' | 'general';
  ocrText?: string;
  extractedValue?: number;
  label?: string;
  notes?: string;
  entryId?: string;
  wellId?: string;
}

export interface PhotoGalleryProps {
  wellId?: string;
  photos: Photo[];
  onPhotoDelete?: (photoId: string) => void;
  onAddPhoto?: (photo: Photo) => void;
  allowDelete?: boolean;
  title?: string;
}

export default function PhotoGallery({
  wellId,
  photos,
  onPhotoDelete,
  onAddPhoto,
  allowDelete = false,
  title = 'Photo Gallery',
}: PhotoGalleryProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filterType, setFilterType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'type'>('newest');

  // Filter and sort photos
  const filteredPhotos = photos
    .filter(photo => {
      // Filter by type
      if (filterType !== 'all' && photo.type !== filterType) return false;
      
      // Filter by search term
      if (searchTerm && !photo.label?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !photo.notes?.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      } else if (sortBy === 'oldest') {
        return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      } else {
        return a.type.localeCompare(b.type);
      }
    });

  // Group photos by date
  const photosByDate = filteredPhotos.reduce((acc, photo) => {
    const date = new Date(photo.timestamp).toLocaleDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(photo);
    return acc;
  }, {} as Record<string, Photo[]>);

  const openLightbox = (photo: Photo) => {
    setSelectedPhoto(photo);
    setCurrentIndex(filteredPhotos.findIndex(p => p.id === photo.id));
  };

  const closeLightbox = () => {
    setSelectedPhoto(null);
  };

  const navigatePhoto = (direction: 'prev' | 'next') => {
    const newIndex = direction === 'prev' 
      ? (currentIndex - 1 + filteredPhotos.length) % filteredPhotos.length
      : (currentIndex + 1) % filteredPhotos.length;
    
    setCurrentIndex(newIndex);
    setSelectedPhoto(filteredPhotos[newIndex]);
  };

  const downloadPhoto = (photo: Photo) => {
    const link = document.createElement('a');
    link.href = photo.dataUrl;
    link.download = `photo-${photo.id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Photo downloaded');
  };

  const handleDelete = (photoId: string) => {
    if (onPhotoDelete) {
      onPhotoDelete(photoId);
      if (selectedPhoto?.id === photoId) {
        closeLightbox();
      }
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'gauge': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'equipment': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'maintenance': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'general': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'gauge': return <Zap className="h-3 w-3" />;
      case 'equipment': return <ImageIcon className="h-3 w-3" />;
      case 'maintenance': return <ImageIcon className="h-3 w-3" />;
      default: return <ImageIcon className="h-3 w-3" />;
    }
  };

  if (photos.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-gray-500">
            <ImageIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No photos yet</p>
            <p className="text-sm mt-2">Photos will appear here as you add them</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <ImageIcon className="h-5 w-5 text-blue-600" />
              <span>{title}</span>
              <Badge variant="outline">{filteredPhotos.length}</Badge>
            </CardTitle>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="h-9 w-9"
              >
                {viewMode === 'grid' ? <List className="h-4 w-4" /> : <Grid3x3 className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Filters and Search */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search photos..."
                  className="pl-9"
                />
              </div>
            </div>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="h-10 px-3 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Types</option>
              <option value="gauge">Gauge Readings</option>
              <option value="equipment">Equipment</option>
              <option value="maintenance">Maintenance</option>
              <option value="general">General</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="h-10 px-3 border border-gray-300 rounded-lg text-sm"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="type">By Type</option>
            </select>
          </div>

          {/* Photos Grid/List */}
          {viewMode === 'grid' ? (
            <div className="space-y-6">
              {Object.entries(photosByDate).map(([date, datePhotos]) => (
                <div key={date}>
                  <div className="flex items-center space-x-2 mb-3">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-700">{date}</h3>
                    <div className="flex-1 border-b border-gray-200"></div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {datePhotos.map((photo) => (
                      <div
                        key={photo.id}
                        className="group relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
                        onClick={() => openLightbox(photo)}
                      >
                        <img
                          src={photo.dataUrl}
                          alt={photo.label || 'Well photo'}
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="absolute bottom-0 left-0 right-0 p-2">
                            <div className="flex items-center justify-between">
                              <Badge className={`text-xs ${getTypeColor(photo.type)}`}>
                                <span className="flex items-center space-x-1">
                                  {getTypeIcon(photo.type)}
                                  <span className="capitalize">{photo.type}</span>
                                </span>
                              </Badge>
                              
                              {allowDelete && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(photo.id);
                                  }}
                                  className="h-7 w-7 text-white hover:bg-red-500/50"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* OCR Badge */}
                        {photo.extractedValue && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-md text-xs font-bold flex items-center space-x-1 shadow-lg">
                            <Zap className="h-3 w-3" />
                            <span>{photo.extractedValue}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPhotos.map((photo) => (
                <Card
                  key={photo.id}
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => openLightbox(photo)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={photo.dataUrl}
                          alt={photo.label || 'Well photo'}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <Badge className={`text-xs ${getTypeColor(photo.type)}`}>
                            <span className="flex items-center space-x-1">
                              {getTypeIcon(photo.type)}
                              <span className="capitalize">{photo.type}</span>
                            </span>
                          </Badge>
                          
                          {photo.extractedValue && (
                            <Badge className="text-xs bg-green-100 text-green-800 border-green-200">
                              <Zap className="h-3 w-3 mr-1" />
                              {photo.extractedValue}
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-900 truncate">
                          {photo.label || 'Untitled Photo'}
                        </p>
                        
                        <p className="text-xs text-gray-500">
                          {new Date(photo.timestamp).toLocaleString()}
                        </p>
                      </div>

                      {allowDelete && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(photo.id);
                          }}
                          className="h-8 w-8 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lightbox Modal */}
      {selectedPhoto && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center">
          <div className="relative w-full h-full max-w-7xl flex flex-col p-4">
            {/* Header */}
            <div className="flex items-start justify-between mb-4 text-white">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Badge className={`${getTypeColor(selectedPhoto.type)}`}>
                    <span className="flex items-center space-x-1">
                      {getTypeIcon(selectedPhoto.type)}
                      <span className="capitalize">{selectedPhoto.type}</span>
                    </span>
                  </Badge>
                  
                  {selectedPhoto.extractedValue && (
                    <Badge className="bg-green-500 text-white border-green-400">
                      <Zap className="h-3 w-3 mr-1" />
                      Reading: {selectedPhoto.extractedValue}
                    </Badge>
                  )}
                </div>
                
                <p className="font-semibold text-lg">
                  {selectedPhoto.label || 'Photo'}
                </p>
                <p className="text-sm text-gray-300">
                  {new Date(selectedPhoto.timestamp).toLocaleString()}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {currentIndex + 1} of {filteredPhotos.length}
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => downloadPhoto(selectedPhoto)}
                  className="text-white hover:bg-white/20 h-10 w-10"
                >
                  <Download className="h-5 w-5" />
                </Button>
                
                {allowDelete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(selectedPhoto.id)}
                    className="text-white hover:bg-red-500/50 h-10 w-10"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeLightbox}
                  className="text-white hover:bg-white/20 h-10 w-10"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Image Container */}
            <div className="flex-1 flex items-center justify-center relative overflow-hidden">
              <img
                src={selectedPhoto.dataUrl}
                alt={selectedPhoto.label || 'Photo'}
                className="max-w-full max-h-full object-contain rounded-lg"
              />

              {/* Navigation Buttons */}
              {filteredPhotos.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigatePhoto('prev')}
                    className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/50 hover:bg-black/70 text-white"
                  >
                    <ChevronLeft className="h-8 w-8" />
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigatePhoto('next')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/50 hover:bg-black/70 text-white"
                  >
                    <ChevronRight className="h-8 w-8" />
                  </Button>
                </>
              )}
            </div>

            {/* Footer with OCR Text */}
            {selectedPhoto.ocrText && (
              <div className="mt-4 bg-gray-900/80 backdrop-blur-sm rounded-lg p-4 text-white">
                <div className="flex items-center space-x-2 mb-2">
                  <Zap className="h-4 w-4 text-green-400" />
                  <span className="text-sm font-semibold">OCR Text:</span>
                </div>
                <p className="text-sm text-gray-300 font-mono bg-black/30 p-3 rounded max-h-32 overflow-y-auto">
                  {selectedPhoto.ocrText}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}